/**
 * ApprovalHeaderActions component.
 *
 * Lifted out of `ApprovalCardHeader` so the "View agent session" icon
 * and the more-actions (3-dot) dropdown can live on the SidePanel's
 * top header row — alongside the maximize and close buttons — rather
 * than inside the card body. Keeps the inner card calmer (just tool
 * icon + display name + timestamp + ownership + Release) and
 * consolidates all chrome controls into one header bar.
 *
 * Owns the "View JSON" dialog locally because the dropdown menu item
 * opens it and that's an item-scoped piece of UI state.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { MoreHorizontal, Copy, FileJson, ShieldAlert, MessageSquare, MessagesSquare, Workflow, History, Loader2 } from 'lucide-react'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui'
import { useSessionsStore } from '@/features/sessions/store'
import { useUIStore } from '@/shared/stores/ui-store'
import { fetchInteractionDetail } from '@/features/inbox-manager/api'
import { getMessageForInteraction } from '@/features/inbox-manager/components/MessageView'
import { useQueueStore } from '../../store'
import type { AttentionItem } from '../../types'

/**
 * Mirror of `TURN_CAP` in `AgentSessionModalBody.tsx`. Kept in sync so
 * the prefetch on hover/focus uses the same batch size as the modal's
 * initial load — otherwise the modal fires a redundant second request.
 */
const PREFETCH_LIMIT = 200

export interface ApprovalHeaderActionsProps {
  /** The attention item these actions act on. */
  item: AttentionItem
  /** Agent session id linked to this item, if any. */
  sessionId?: string | null
  /** Called when Escalate is clicked from the dropdown. */
  onEscalate?: () => void
}

/**
 * Extract tool call args from `item.details`. For agent_tool_approval items
 * the Go operations service stores the full pending_attention dict as
 * `item.details`, which includes an `args` key with the tool call arguments.
 */
function extractArgsFromDetails(item: AttentionItem): Record<string, unknown> | null {
  if (!item.details || typeof item.details !== 'object') return null
  const args = (item.details as Record<string, unknown>).args
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null
  return args as Record<string, unknown>
}

/**
 * Best-effort extraction of a customer email from the tool args embedded in
 * `item.details`. Used as a fallback when `item.customer_email` is absent
 * (e.g. the operations backend hasn't surfaced it yet, or it's an older item).
 *
 * Resolution order:
 *   1. args.customer_email — generic field used by some tools.
 *   2. The recipient address — for send_email approvals this is the customer
 *      the agent is replying to (always a clean email string, no "Name <email>"
 *      wrapping). The `send_email` tool names this parameter `to_email` (see
 *      communications.py), but other tools / older items use `to`, `recipient`,
 *      or `recipient_email`, so all aliases are checked. This list mirrors the
 *      recipient resolution in reject/sourceContext.ts and the email-field
 *      classifier so the History button and the rest of the UI agree on what
 *      counts as the recipient.
 *   3. trigger_payload.email.from — the inbound sender for email-event
 *      workflows. May be in "Name <email>" format; the angle-bracket portion
 *      is extracted when present.
 */
const RECIPIENT_ARG_KEYS = ['to_email', 'to', 'recipient', 'recipient_email', 'reply_to_email'] as const

function extractCustomerEmailFromDetails(item: AttentionItem): string | null {
  const args = extractArgsFromDetails(item)
  if (args) {
    const customerEmail = args['customer_email']
    if (typeof customerEmail === 'string' && customerEmail.length > 0) {
      console.debug('[ApprovalHeaderActions] customer email from args.customer_email', { id: item.id, email: customerEmail })
      return customerEmail
    }

    // send_email and friends: the recipient is the customer being replied to.
    for (const key of RECIPIENT_ARG_KEYS) {
      const value = args[key]
      if (typeof value === 'string' && value.length > 0) {
        console.debug(`[ApprovalHeaderActions] customer email from args.${key}`, { id: item.id, email: value })
        return value
      }
    }
  }

  // Fallback: parse the inbound sender from the trigger payload
  if (item.details && typeof item.details === 'object') {
    const tp = (item.details as Record<string, unknown>)['trigger_payload']
    if (tp && typeof tp === 'object' && !Array.isArray(tp)) {
      const email = (tp as Record<string, unknown>)['email']
      if (email && typeof email === 'object' && !Array.isArray(email)) {
        const from = (email as Record<string, unknown>)['from']
        if (typeof from === 'string' && from.length > 0) {
          // Handle "Name <addr@example.com>" format
          const match = from.match(/<([^>]+)>/)
          const resolved = match ? match[1] : from
          console.debug('[ApprovalHeaderActions] customer email from trigger_payload.email.from', { id: item.id, raw: from, resolved })
          return resolved
        }
      }
    }
  }

  console.debug('[ApprovalHeaderActions] no customer email found', {
    id: item.id,
    customer_email: item.customer_email,
    args: extractArgsFromDetails(item),
    has_trigger_payload: !!(item.details && (item.details as Record<string, unknown>)['trigger_payload']),
  })
  return null
}

/**
 * Best-effort extraction of a customer_id from the tool args embedded in
 * `item.details`. Only used as a last-resort gate check — the RevenueCat
 * anonymous ID stored here cannot be used for the interactions API lookup,
 * but its presence signals we have enough customer context to show the menu
 * entry (the email will come from `item.customer_email` or args above).
 */
function extractCustomerIdFromDetails(item: AttentionItem): string | null {
  const args = extractArgsFromDetails(item)
  if (!args) return null
  const val = args['customer_id']
  return typeof val === 'string' && val.length > 0 ? val : null
}

/**
 * Best-effort extraction of the inbound interaction id a `send_email` approval
 * is replying to. Agent-sourced send_email approvals under the canonical
 * attention-item framework (Task 4065a) carry NO trigger_payload and often no
 * literal recipient email — the inbound message is referenced only by
 * `args.interaction_id` (see buildSyntheticDetail.ts). That interaction's
 * sender IS the customer, so its presence is enough to offer the History
 * button; the email itself is resolved lazily from the inbox API on click.
 *
 * Also reads `details.interaction_id` (top-level) which is set by the Go
 * operations backend enrichment pass (Task 4084) for execution-backed items
 * (e.g. issue_stripe_refund, escalate_to_operator) that carry no recipient
 * arg but whose originating execution holds the inbound interaction id.
 */
function extractInteractionIdFromDetails(item: AttentionItem): string | null {
  // Prefer args.interaction_id (agent-set, send_email / explicit tool args).
  const args = extractArgsFromDetails(item)
  if (args) {
    const val = args['interaction_id']
    if (typeof val === 'string' && val.trim().length > 0) return val
  }
  // Fallback: top-level details.interaction_id set by the operations enrichment
  // pass for execution-backed tools that have no recipient arg.
  if (item.details && typeof item.details === 'object') {
    const val = (item.details as Record<string, unknown>)['interaction_id']
    if (typeof val === 'string' && val.trim().length > 0) return val
  }
  return null
}

function asNonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null
}

/**
 * Best-effort extraction of the review interaction id for a review-backed
 * attention item.
 *
 * App Store review workflows are triggered by an `interaction.received` event
 * whose interaction id the operations executor stashes as the
 * `interaction_id` facet (see automation_executor.go — the event id is a
 * generated `evt_…` that differs from the interaction id, so the facet is the
 * canonical reference). The serializer surfaces facets verbatim under
 * `details.facets`, and the original trigger payload under
 * `details.trigger_payload`.
 *
 * Returns the interaction id only when the item is actually a review (the
 * trigger payload `type` is `"review"`), so the "View review thread" affordance
 * never shows up on non-review approvals.
 */
function extractReviewInteractionId(item: AttentionItem): string | null {
  if (!item.details || typeof item.details !== 'object') return null
  const details = item.details as Record<string, unknown>

  // Gate on the interaction being a review.
  const tp = details['trigger_payload']
  let isReview = false
  if (tp && typeof tp === 'object' && !Array.isArray(tp)) {
    const tpType = (tp as Record<string, unknown>)['type']
    if (tpType === 'review') isReview = true
    // A `review` sub-object is also a strong signal even if `type` is absent.
    if (!isReview && (tp as Record<string, unknown>)['review']) isReview = true
  }
  if (!isReview) return null

  // Prefer the indexed facet; fall back to the trigger payload id.
  const facets = details['facets']
  if (facets && typeof facets === 'object' && !Array.isArray(facets)) {
    const id = (facets as Record<string, unknown>)['interaction_id']
    if (typeof id === 'string' && id.length > 0) return id
  }
  if (tp && typeof tp === 'object' && !Array.isArray(tp)) {
    const id = (tp as Record<string, unknown>)['id']
    if (typeof id === 'string' && id.length > 0) return id
  }
  return null
}

export function ApprovalHeaderActions({
  item,
  sessionId,
  onEscalate,
}: ApprovalHeaderActionsProps) {
  const [isJsonModalOpen, setIsJsonModalOpen] = React.useState(false)

  // Customer context for the Interaction History popout.
  //
  // Resolution order for the email:
  //   1. item.customer_email — set by the Go operations service when it can
  //      extract a real email from the tool args (requires ops backend ≥ ba132920e).
  //   2. customer_email arg inside item.details.args — same data, but read
  //      directly from the stored tool args as a client-side fallback so the
  //      feature works even if the ops backend hasn't been deployed yet or the
  //      item predates the Go-side extraction logic.
  //
  // historyCustomerId is kept as a last-resort gate only: the RevenueCat
  // anonymous ID it typically holds cannot be used for the interactions API,
  // but its presence still signals we have a customer to look up.
  //
  //   3. historyInteractionId — agent-sourced send_email approvals often carry
  //      NO literal email at all, only `args.interaction_id` referencing the
  //      inbound message. We can't read an email from that synchronously, so the
  //      button still renders and the email is resolved from the inbox API the
  //      moment it's clicked (and prefetched on hover so the click is instant).
  const historyCustomerEmail =
    item.customer_email ||
    extractCustomerEmailFromDetails(item) ||
    undefined
  const historyCustomerId = !historyCustomerEmail
    ? extractCustomerIdFromDetails(item) ?? undefined
    : undefined
  // Last-resort context: an inbound interaction id we can resolve to a customer
  // email on demand. Only consulted when no email/id is available synchronously.
  const historyInteractionId =
    !historyCustomerEmail && !historyCustomerId
      ? extractInteractionIdFromDetails(item) ?? undefined
      : undefined
  const hasCustomerContext = !!(
    historyCustomerEmail ||
    historyCustomerId ||
    historyInteractionId
  )

  // Review thread context for the Review Thread popout. Only review-backed
  // attention items resolve to a non-null interaction id.
  const reviewInteractionId = extractReviewInteractionId(item)
  const hasReviewThread = !!reviewInteractionId

  const loadTranscriptChunk   = useSessionsStore((s) => s.loadTranscriptChunk)
  const openAgentSessionModal  = useQueueStore((s) => s.openAgentSessionModal)
  const openCustomerHistoryModal = useQueueStore((s) => s.openCustomerHistoryModal)
  const openReviewThreadModal  = useQueueStore((s) => s.openReviewThreadModal)
  const openDiscussModal       = useUIStore((s) => s.openDiscussModal)

  const handleOpenAgentSession = React.useCallback(() => {
    if (!sessionId) return
    openAgentSessionModal(sessionId)
  }, [sessionId, openAgentSessionModal])

  // Pre-fetch the transcript when the operator hovers or focuses the
  // button — by the time they click, the request is in flight or done
  // so the modal opens instantly. The store call is idempotent.
  const prefetchTranscript = React.useCallback(() => {
    if (!sessionId) return
    loadTranscriptChunk(sessionId, PREFETCH_LIMIT)
  }, [sessionId, loadTranscriptChunk])

  // Loading state for the on-click email resolution (interaction_id path only).
  const [isResolvingHistory, setIsResolvingHistory] = React.useState(false)
  // Caches the email resolved from `historyInteractionId` so a successful
  // hover-prefetch makes the subsequent click open the popout instantly, and a
  // second click never re-fetches. `undefined` = not attempted, `null` =
  // attempted but nothing resolved (e.g. the fetch failed or had no sender).
  const resolvedHistoryEmail = React.useRef<string | null | undefined>(undefined)

  // Resolve the customer's email from the inbound interaction. Idempotent and
  // cached: safe to call repeatedly (on hover and again on click).
  const resolveEmailFromInteraction = React.useCallback(async (): Promise<string | null> => {
    if (resolvedHistoryEmail.current !== undefined) return resolvedHistoryEmail.current
    if (!historyInteractionId) return null
    try {
      const { interaction, messages } = await fetchInteractionDetail(historyInteractionId)
      const message = getMessageForInteraction(messages, interaction ?? undefined)
      // The inbound interaction's sender is the customer. Prefer the
      // interaction's own `from` (authoritative for the referenced inbound
      // message) over the resolved thread message, whose fallback can land on
      // an outbound reply ("us") when no exact id match exists.
      const email =
        asNonEmptyString(interaction?.from) ??
        asNonEmptyString(message?.from)
      resolvedHistoryEmail.current = email
      return email
    } catch (err) {
      console.error('[ApprovalHeaderActions] failed to resolve customer email from interaction', {
        id: item.id,
        interactionId: historyInteractionId,
        err,
      })
      resolvedHistoryEmail.current = null
      return null
    }
  }, [historyInteractionId, item.id])

  // Warm the interaction lookup on hover/focus so the popout opens instantly
  // when there's no synchronously-available email. No-op when we already have
  // an email/id (nothing to resolve) or no interaction id to resolve from.
  const prefetchHistory = React.useCallback(() => {
    if (historyCustomerEmail || historyCustomerId || !historyInteractionId) return
    void resolveEmailFromInteraction()
  }, [historyCustomerEmail, historyCustomerId, historyInteractionId, resolveEmailFromInteraction])

  // Open the customer history popout window.
  const handleOpenHistory = React.useCallback(async () => {
    if (!hasCustomerContext) return

    // Fast path: an email or id was available synchronously — open immediately.
    if (historyCustomerEmail || historyCustomerId) {
      openCustomerHistoryModal({
        email:      historyCustomerEmail,
        customerId: historyCustomerId,
        name:       item.customer_name ?? undefined,
      })
      return
    }

    // Slow path: only an interaction_id is available. Resolve the customer's
    // email from the inbox API (served instantly from cache if a hover-prefetch
    // already ran), then open the popout. If resolution yields nothing we still
    // open — the popout shows its own empty state rather than the click dying.
    setIsResolvingHistory(true)
    const email = await resolveEmailFromInteraction()
    setIsResolvingHistory(false)
    openCustomerHistoryModal({
      email:      email ?? undefined,
      customerId: undefined,
      name:       item.customer_name ?? undefined,
    })
  }, [
    hasCustomerContext,
    historyCustomerEmail,
    historyCustomerId,
    item.customer_name,
    openCustomerHistoryModal,
    resolveEmailFromInteraction,
  ])

  // Open the review thread popout window.
  const handleOpenReviewThread = React.useCallback(() => {
    if (!reviewInteractionId) return
    openReviewThreadModal({
      interactionId: reviewInteractionId,
      reviewerName:  item.customer_name ?? null,
      appName:       item.subtitle ?? null,
    })
  }, [reviewInteractionId, item.customer_name, item.subtitle, openReviewThreadModal])

  // Discuss-modal handlers. Only meaningful when the AttentionItem is
  // backed by an execution — every other case hides the menu item outright.
  const executionId = item.execution_id ?? null
  const handleOpenDiscuss = React.useCallback(() => {
    if (!executionId) return
    openDiscussModal({ executionId, attentionItemId: item.id })
  }, [executionId, item.id, openDiscussModal])

  // Best-effort warm-up for the Discuss modal: pre-pull the transcript on
  // hover so context assembles faster when the item is opened.
  const prefetchDiscussCtx = React.useCallback(() => {
    if (sessionId) loadTranscriptChunk(sessionId, PREFETCH_LIMIT)
  }, [sessionId, loadTranscriptChunk])

  return (
    <>
      {sessionId && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenAgentSession}
              onMouseEnter={prefetchTranscript}
              onFocus={prefetchTranscript}
              aria-label="View agent session"
              data-testid="header-view-agent-session-button"
            >
              <MessageSquare style={iconStyles.icon16} aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View agent session</TooltipContent>
        </Tooltip>
      )}

      {hasCustomerContext && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenHistory}
              onMouseEnter={prefetchHistory}
              onFocus={prefetchHistory}
              disabled={isResolvingHistory}
              aria-label="View interaction history"
              aria-busy={isResolvingHistory}
              data-testid="header-interaction-history-button"
            >
              {isResolvingHistory ? (
                <Loader2 style={iconStyles.icon16} className="animate-spin" aria-hidden="true" />
              ) : (
                <History style={iconStyles.icon16} aria-hidden="true" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Interaction history</TooltipContent>
        </Tooltip>
      )}

      {hasReviewThread && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenReviewThread}
              aria-label="View review thread"
              data-testid="header-review-thread-button"
            >
              <MessagesSquare style={iconStyles.icon16} aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Review thread</TooltipContent>
        </Tooltip>
      )}

      {executionId && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenDiscuss}
              onMouseEnter={prefetchDiscussCtx}
              aria-label="Discuss execution"
              data-testid="header-discuss-execution-button"
            >
              <Workflow style={iconStyles.icon16} aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Discuss execution</TooltipContent>
        </Tooltip>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="More actions">
            <MoreHorizontal style={iconStyles.icon16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" style={{ width: '192px' }}>
          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(item.id)}>
            <Copy style={iconStyles.icon14} />
            <span>Copy ID</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setIsJsonModalOpen(true)}>
            <FileJson style={iconStyles.icon14} />
            <span>View JSON</span>
          </DropdownMenuItem>
          {onEscalate && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onEscalate} style={{ color: 'var(--error)' }}>
                <ShieldAlert style={iconStyles.icon14} />
                <span>Escalate</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isJsonModalOpen} onOpenChange={setIsJsonModalOpen}>
        <DialogContent style={styles.jsonDialogContent}>
          <DialogHeader>
            <DialogTitle>Raw JSON</DialogTitle>
            <DialogDescription style={{ display: 'none' }}>
              Raw JSON representation of the attention item
            </DialogDescription>
          </DialogHeader>
          <pre style={styles.jsonPre}>
            {JSON.stringify(item, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

    </>
  )
}

const iconStyles: Record<string, CSSProperties> = {
  icon16: {
    width: '16px',
    height: '16px',
  },
  icon14: {
    width: '14px',
    height: '14px',
    marginRight: '8px',
  },
}

const styles: Record<string, CSSProperties> = {
  jsonDialogContent: {
    maxWidth: '800px',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  jsonPre: {
    fontSize: '12px',
    background: 'var(--bg-tertiary)',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}

export default ApprovalHeaderActions
